import type { Item, Layer, Part, Section } from '../../domain';
import { cmToMeters } from '../../domain';
import {
  IDENTITY,
  boxTransform,
  makeNode,
  translate,
  type SceneGraph,
  type SceneNode,
} from '../SceneGraph';

const SECTION_DEPTH = 0.0015; // толщина полотна (васи ~1.5 мм), м
const RAIL_THICKNESS = 0.02; // толщина рейки-плейсхолдера, м (реальная рейка тоньше)
import type { SystemBuilder } from '../SystemBuilder';

/**
 * Базовый билдер: общий каркас сборки (parts → layers → sections + рейки),
 * специфика типа — в наследниках (top/hidePanel/управление). Все размеры — в
 * метрах (вход в см, D7). Выдаёт нейтральный SceneGraph.
 */
export abstract class BaseSystemBuilder implements SystemBuilder<Item> {
  build(item: Item): SceneGraph {
    const children: SceneNode[] = [...this.buildItemExtras(item)];

    // Раскладка частей по ширине, центрируя всю сборку вокруг x = 0 (чтобы
    // совпадать с деталями уровня изделия — короб/панель/крепления).
    const totalW = item.parts.reduce((acc, p) => acc + cmToMeters(p.width), 0);
    let x = -totalW / 2;
    item.parts.forEach((part, iP) => {
      const w = cmToMeters(part.width);
      children.push(this.buildPart(item, part, iP, x + w / 2));
      x += w;
    });

    return {
      root: makeNode({
        id: 'root',
        transform: IDENTITY,
        tags: ['system', item.model],
        children,
      }),
    };
  }

  /** Узлы уровня изделия (напр. верхняя панель, крепления). По умолчанию нет. */
  protected buildItemExtras(_item: Item): SceneNode[] {
    return [];
  }

  protected buildPart(item: Item, part: Part, iP: number, x: number): SceneNode {
    const layers = part.layers.map((layer, iL) => this.buildLayer(item, part, layer, iP, iL));
    return makeNode({
      id: `part${iP}`,
      transform: translate([x, 0, 0]),
      tags: ['part'],
      children: layers,
    });
  }

  protected buildLayer(item: Item, part: Part, layer: Layer, iP: number, iL: number): SceneNode {
    const children: SceneNode[] = [];
    const top = this.buildTop(item, part, layer, iP, iL);
    if (top) children.push(top);
    children.push(...this.buildSections(layer, iP, iL));
    const control = this.buildControl(item, layer, iP, iL);
    if (control) children.push(control);
    return makeNode({
      id: `part${iP}.layer${iL}`,
      transform: translate([0, 0, cmToMeters(0.2) * iL]),
      tags: ['layer'],
      children,
    });
  }

  /** Секции полотна снизу вверх + промежуточная рейка на каждой границе. */
  protected buildSections(layer: Layer, iP: number, iL: number): SceneNode[] {
    const out: SceneNode[] = [];
    let y = 0; // метры, от низа
    layer.sections.forEach((section, i) => {
      const h = cmToMeters(section.height);
      out.push(this.buildSection(layer, section, iP, iL, i, y + h / 2));
      out.push(this.buildRail(layer, iP, iL, i, y));
      y += h;
    });
    return out;
  }

  protected buildSection(
    _layer: Layer,
    section: Section,
    iP: number,
    iL: number,
    i: number,
    yCenter: number,
  ): SceneNode {
    const w = cmToMeters(section.width);
    const h = cmToMeters(section.height);
    return makeNode({
      id: `sec.${iP}.${iL}.${i}`,
      meshId: 'section',
      materialId: section.materialColor ?? undefined,
      transform: boxTransform([0, yCenter, 0], [w, h, SECTION_DEPTH]),
      tags: ['section'],
    });
  }

  protected buildRail(layer: Layer, iP: number, iL: number, i: number, y: number): SceneNode {
    const w = cmToMeters(layer.width);
    return makeNode({
      id: `rail.${iP}.${iL}.${i}`,
      meshId: 'rail',
      materialId: layer.railColor ?? undefined,
      transform: boxTransform([0, y, 0], [w, RAIL_THICKNESS, RAIL_THICKNESS]),
      // Реальная рейка нормирована по длине — тянем только по X, профиль натуральный.
      fit: { mode: 'stretch', axis: 'x', length: w },
      tags: ['rail'],
    });
  }

  protected abstract buildTop(
    item: Item,
    part: Part,
    layer: Layer,
    iP: number,
    iL: number,
  ): SceneNode | null;

  protected abstract buildControl(
    item: Item,
    layer: Layer,
    iP: number,
    iL: number,
  ): SceneNode | null;
}
